import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { layoutModeTypes } from "../../Components/constants/layout";
import logoDark from '../../assets/images/exiaLogo_dark.png';
import logoLight from '../../assets/images/exiaLogo_dark.png';
import { useAuth } from '../../hooks/useAuth';
import { oauthService } from '../../services/oauthService';
import { saveAuthData } from '../../services/authService';

const OAuthCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { updateAuthState, setUser, setAuthMethod, handleOAuthAuth } = useAuth();
    const [status, setStatus] = useState('processing');
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState(null);

    // Layout state selector
    const selectLayoutState = (state) => state.Layout;
    const layoutData = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutModeType: layout.layoutModeType,
        })
    );
    const { layoutModeType } = useSelector(layoutData);

    useEffect(() => {
        // Prevent duplicate calls
        let isProcessing = false;
        
        const handleCallback = async () => {
            // Prevent duplicate processing
            if (isProcessing) {
                console.log('OAuth callback already processing, skipping duplicate call');
                return;
            }
            
            isProcessing = true;
            
            try {
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const errorParam = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');

                console.log('OAuth callback received:', { code, state, errorParam, errorDescription });

                // Handle OAuth errors
                if (errorParam) {
                    throw new Error(errorDescription || errorParam);
                }

                // Handle OAuth callback with code and state
                if (code && state) {
                    console.log('Processing OAuth callback with code and state');
                    setStatus('processing');
                    
                    try {
                        // Call backend to verify OAuth code and generate JWT
                        const result = await oauthService.handleCallback(code, state);
                        console.log('OAuth verification result:', result);
                        
                        if (result.success) {
                            // Extract tokens and user data (same format as regular login)
                            const { token: access_token, refreshToken: refresh_token, user: userData } = result;
                            
                            console.log('Storing tokens exactly like regular login...');
                            
                            // Store tokens in sessionStorage with the SAME keys as regular login
                            sessionStorage.setItem("authToken", access_token);
                            sessionStorage.setItem("refreshToken", refresh_token);
                            sessionStorage.setItem("user", JSON.stringify(userData));
                            
                            console.log('✅ Tokens stored in sessionStorage with regular login keys');
                            
                            // Set user info in component state
                            setUserInfo(userData);
                            
                            // Update auth context properly
                            if (updateAuthState) {
                                updateAuthState(userData, 'oauth');
                            } else if (setUser && setAuthMethod) {
                                setUser(userData);
                                setAuthMethod('oauth');
                            }
                            
                            setStatus('success');
                            
                            // Redirect to temporary debug page instead of home
                            setTimeout(() => {
                                navigate('/oauth-debug', { 
                                    state: { 
                                        access_token, 
                                        refresh_token, 
                                        userData,
                                        auth_method: 'oauth'
                                    } 
                                });
                            }, 1500);
                            
                            return;
                        } else {
                            throw new Error(result.message || 'OAuth verification failed');
                        }
                    } catch (error) {
                        console.error('OAuth verification error:', error);
                        setError(error.message || 'OAuth verification failed');
                        setStatus('error');
                    }
                } else {
                    throw new Error('Missing OAuth code or state parameters');
                }
            } catch (error) {
                console.error('OAuth callback error:', error);
                setError(error.message || 'OAuth callback failed');
                setStatus('error');
            } finally {
                isProcessing = false;
            }
        };

        handleCallback();
        
        // Cleanup function
        return () => {
            isProcessing = false;
        };
    }, [searchParams, navigate]); // Removed function dependencies that cause re-renders

    // Show loading state
    if (status === 'processing') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.DARKMODE ? logoDark : logoLight}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <Spinner color="primary" className="me-2" />
                                                    <span>Processing OAuth callback...</span>
                                                </div>
                                                <p className="text-muted">
                                                    Please wait while we complete your authentication.
                                                </p>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    // Show error state
    if (status === 'error') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.DARKMODE ? logoDark : logoLight}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <div className="avatar-md mx-auto">
                                                        <div className="avatar-title bg-danger-subtle text-danger fs-36 rounded-circle">
                                                            <i className='bx bx-error-circle'></i>
                                                        </div>
                                                    </div>
                                                    <h5 className="mt-3 mb-2">OAuth Authentication Failed</h5>
                                                    <p className="text-muted mb-4">{error}</p>
                                                </div>
                                                <div className="d-grid gap-2">
                                                    <Link to="/login/cognito" className="btn btn-primary">
                                                        Try Again
                                                    </Link>
                                                    <Link to="/login" className="btn btn-outline-secondary">
                                                        Back to Login
                                                    </Link>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    // Show success state
    if (status === 'success') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.DARKMODE ? logoDark : logoLight}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <div className="avatar-md mx-auto">
                                                        <div className="avatar-title bg-success-subtle text-success fs-36 rounded-circle">
                                                            <i className='bx bx-check-circle'></i>
                                                        </div>
                                                    </div>
                                                    <h5 className="mt-3 mb-2">Authentication Successful!</h5>
                                                    <p className="text-muted mb-4">
                                                        Welcome back, {userInfo?.name || 'User'}!
                                                    </p>
                                                </div>
                                                <div className="d-grid">
                                                    <div className="text-muted mb-3">
                                                        Redirecting to home page...
                                                    </div>
                                                    <Spinner color="primary" size="sm" />
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    return null;
};

export default OAuthCallback;